import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';

import { getCommissions } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import { Commission as CommissionType } from '../../types';
import { formatDateShort } from '../../utils/formatShortDate';
import AnalyticsHeader from '../../widgets/Header';

export const COMMISSIONS_QUERY_KEY = ['commissions'];

const columnHelper = createColumnHelper<CommissionType>();

const columns = [
  columnHelper.accessor(
    value => {
      return value.driver ? value.driver.name : value.motorboy.name;
    },
    {
      header: 'Name',
      id: 'name',
      cell: info => info.getValue(),
      footer: info => info.column.id
    }
  ),
  columnHelper.accessor('modeOfPayment', {
    header: 'Mode of Payment',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('personnelType', {
    header: 'Personnel Type',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('total', {
    header: 'Total',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id
  }),
  columnHelper.accessor('productsCases', {
    header: 'Cases',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  })
];

interface CommissionsTableProps {
  query?: Record<string, unknown>;
}

const limit = 100;

const CommissionsTable = ({ query }: CommissionsTableProps) => {
  const [page, setPage] = useState(1);
  const compositeQueryKey = query
    ? [...COMMISSIONS_QUERY_KEY, { ...query, page, limit }]
    : [COMMISSIONS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getCommissions({ ...query, page, limit })
  });
  const commissions = data?.results ?? [];

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { count } = data ?? {};

  const totalPages = Math.ceil(count! / limit);

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <AnalyticsHeader title="Commissions" />
      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : commissions.length > 0 ? (
                <>
                  <TableList commissions={commissions} columns={columns} />
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages ?? 0}
                    onPageChange={handlePageChange}
                    nextPage={nextPage ?? null}
                    prevPage={prevPage ?? null}
                  />
                </>
              ) : (
                <EmptyTableState message="No commissions yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const Commission = () => {
  return (
    <div className="main-content">
      <CommissionsTable />
    </div>
  );
};

export default Commission;

interface TableListProps {
  commissions: CommissionType[];
  // todo: fix type
  columns: any;
}

const TableList = ({ commissions, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const tableInstance = useReactTable({
    data: commissions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th colSpan={header.colSpan} key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};
